import React from "react";
import { LightForm } from "../../light/types";

interface LightDimensionFormProps {
  form?: LightForm;
  values: Record<string, number | undefined>;
  onChange: (next: Record<string, number | undefined>) => void;
}

const DECIMAL_PATTERN = "[0-9]*([.,][0-9]{0,2})?";

const LABELS: Record<string, string> = {
  longueur: "Longueur (L) (cm) ",
  largeur: "Largeur (l) (cm)",
  hauteur: "Hauteur (h) (cm)",
  diametre: "Diamètre (D) (cm)",
  talon: "Talon (cm)",
  l1: "Largeur haute (l1) (cm)",
  l2: "Largeur basse (l2) (cm)",
  ourlet: "Ourlet (cm)",
};

const COLOR_CLASSES: Record<string, string> = {
  longueur: "text-red-600",
  largeur: "text-green-600",
  hauteur: "text-blue-600",
  diametre: "text-yellow-500",
  talon: "text-amber-600",
  l1: "text-pink-500",
  l2: "text-green-600",
  ourlet: "text-gray-700",
};

const INPUT_BORDER_CLASSES: Record<string, string> = {
  longueur: "border-red-300 focus:border-red-500",
  largeur: "border-green-300 focus:border-green-500",
  hauteur: "border-blue-300 focus:border-blue-500",
  diametre: "border-yellow-300 focus:border-yellow-500",
  talon: "border-amber-300 focus:border-amber-500",
  l1: "border-pink-300 focus:border-pink-500",
  l2: "border-green-300 focus:border-green-500",
  ourlet: "border-gray-300 focus:border-blue-500",
};

const normalizeDecimalInput = (raw: string): { num?: number; text: string } => {
  const trimmed = raw.trim();
  if (!trimmed) return { num: undefined, text: "" };
  const normalized = trimmed.replace(",", ".");
  const num = Number(normalized);
  if (!Number.isFinite(num) || num < 0) {
    return { num: undefined, text: raw };
  }
  const rounded = Math.round(num * 100) / 100;
  const decimalPart = normalized.split(".")[1];
  const hasTooManyDecimals = decimalPart && decimalPart.length > 2;
  return {
    num: rounded,
    text: hasTooManyDecimals ? String(rounded) : normalized,
  };
};

export const LightDimensionForm: React.FC<LightDimensionFormProps> = ({ form, values, onChange }) => {
  const fields = form?.dimensions || [];
  const hasBiseau = fields.includes("l1") || fields.includes("l2");
  const [rawValues, setRawValues] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!form) {
      setRawValues({});
      return;
    }
    setRawValues((prev) => {
      const next: Record<string, string> = {};
      (form.dimensions || []).forEach((field) => {
        if (values[field] !== undefined) {
          next[field] = String(values[field]);
        } else {
          next[field] = prev[field] ?? "";
        }
      });
      return next;
    });
  }, [form, values]);

  const handleChange = (key: string, raw: string) => {
    setRawValues((prev) => ({ ...prev, [key]: raw }));
  };

  const handleBlur = (key: string) => {
    const raw = rawValues[key] ?? "";
    const { num, text } = normalizeDecimalInput(raw);
    setRawValues((prev) => ({ ...prev, [key]: text }));
    onChange({ ...values, [key]: num });
  };

  if (!form) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map((field) => {
          if (hasBiseau && field === "largeur") {
            // largeur disparait si l1/l2 sont attendus
            return null;
          }
          const inputId = `dim-${field}`;
          const colorClass = COLOR_CLASSES[field] || "text-gray-800";
          const borderClass = INPUT_BORDER_CLASSES[field] || "border-gray-300 focus:border-blue-500";
          return (
            <div key={field} className="flex flex-col">
              <label className={`text-sm font-semibold ${colorClass}`} htmlFor={inputId}>
                {LABELS[field] || field}
              </label>
              <input
                id={inputId}
                type="text"
                inputMode="decimal"
                pattern={DECIMAL_PATTERN}
                className={`mt-1 rounded border px-3 py-2 focus:outline-none ${borderClass}`}
                value={rawValues[field] ?? ""}
                onChange={(e) => handleChange(field, e.target.value)}
                onBlur={() => handleBlur(field)}
                required
              />
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="font-semibold">💡 Conseil de saisie des mesures</div>
        <p className="mt-1">
          Selon l’élasticité de la matière et la densité / l’épaisseur de la mousse, il peut être pertinent d’ajuster à la baisse les
          dimensions saisies afin d’anticiper la déformation sous contrainte.
        </p>
        <p className="mt-1">Le talon s’ajoute systématiquement à ces dimensions.</p>
        <p className="mt-1">Les valeurs renseignées servent de base au plan atelier et aux quantités de matière.</p>
      </div>
    </div>
  );
};
